import { useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Chip,
  TextField,
  Typography,
} from '@mui/material';
import SkeletonLoad from 'src/pages/components/SkeletonLoad';
import {
  getMyNotificationEmails,
  setMyNotificationEmails,
} from 'src/apis/eezyguard';

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

function dedupeEmails(list: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  list.forEach((raw) => {
    const email = raw.trim().toLowerCase();
    if (email && EMAIL_RE.test(email) && !seen.has(email)) {
      seen.add(email);
      out.push(email);
    }
  });
  return out;
}

export default function CompanyEmailsTab(): JSX.Element {
  const [emails, setEmails] = useState<string[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    getMyNotificationEmails()
      .then((res) => active && setEmails(res))
      .catch(() => active && setError('Erro ao carregar os e-mails.'))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, []);

  const save = async (next: string[]) => {
    const normalized = dedupeEmails(next);
    setSaving(true);
    setError('');
    try {
      const saved = await setMyNotificationEmails(normalized);
      setEmails(saved);
    } catch {
      setError('Não foi possível salvar os e-mails.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <SkeletonLoad variant="cards" rows={2} />;

  return (
    <Box display="flex" flexDirection="column" gap={1.5}>
      {error && <Alert severity="error">{error}</Alert>}
      <Typography variant="subtitle1" fontWeight={600}>
        E-mails que recebem apenas as notificações
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Estes e-mails recebem por e-mail cada nova denúncia da empresa, mas{' '}
        <strong>não têm acesso ao painel</strong>. Para dar acesso ao painel, crie um
        usuário do tipo &quot;Administrador da empresa&quot;.
      </Typography>
      <Autocomplete
        multiple
        freeSolo
        options={[]}
        value={emails}
        inputValue={input}
        disabled={saving}
        onInputChange={(_, newInput) => {
          if (newInput.includes(',')) {
            const tokens = newInput.split(',');
            const merged = dedupeEmails([...emails, ...tokens.slice(0, -1)]);
            if (merged.length !== emails.length) save(merged);
            setInput(tokens[tokens.length - 1]);
          } else {
            setInput(newInput);
          }
        }}
        onChange={(_, newValue) => {
          save(newValue as string[]);
          setInput('');
        }}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            <Chip
              size="small"
              variant="outlined"
              label={option}
              {...getTagProps({ index })}
              key={option}
            />
          ))
        }
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            placeholder="email@exemplo.com"
            helperText="Para inserir outro e-mail, adicione uma vírgula (,) ou pressione Enter."
            inputProps={{ ...params.inputProps, autoComplete: 'off' }}
          />
        )}
      />
    </Box>
  );
}
